import type { ReactNode } from "react";

import {
  useResolvedConfig,
  type EntityType,
  type ResolvedField,
} from "@/hooks/use-resolved-config";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  CURRENCY_OPTIONS,
  TIMEZONE_OPTIONS,
  WEIGHT_UNIT_OPTIONS,
  configOptions,
  withCurrentValue,
} from "@/lib/config-options";
import { useTranslation } from "@/i18n/use-translation";

function InheritedHint({ field }: { field: ResolvedField | undefined }) {
  const { t } = useTranslation();
  if (!field?.source || !field.source_name) return null;
  return (
    <p className="mt-1 text-xs text-muted-foreground">
      {t("settings.inheritedFrom", {
        source: field.source,
        name: field.source_name,
        value: String(field.value ?? t("settings.notSet")),
      })}
    </p>
  );
}

interface ConfigOverridesProps {
  entityType: EntityType;
  entityId: string | null;
  onCurrencyChange: (value: string) => void;
  onTimezoneChange: (value: string) => void;
  onWeightUnitChange: (value: string) => void;
  onWastePctTargetChange: (value: string) => void;
  onWastePerCoverTargetChange: (value: string) => void;
  currentCurrency?: string;
  currentTimezone?: string;
  currentWeightUnit?: string;
  currentWastePctTarget?: string;
  currentWastePerCoverTarget?: string;
  wastePctTargetError?: string;
  wastePerCoverTargetError?: string;
  /** Fields shown on one entity type rather than four, rendered at the end of the stack.
   *
   * A slot rather than two more props gated on `entityType === "site"`: gated props let a caller
   * pass `entityType="site"` and forget the handlers, and the field then silently does not render.
   * Only the Site dialog fills this, with `ServiceHoursField` (POS2-1210). */
  children?: ReactNode;
}

export function ConfigOverrides({
  entityType,
  entityId,
  onCurrencyChange,
  onTimezoneChange,
  onWeightUnitChange,
  onWastePctTargetChange,
  onWastePerCoverTargetChange,
  currentCurrency,
  currentTimezone,
  currentWeightUnit,
  currentWastePctTarget,
  currentWastePerCoverTarget,
  wastePctTargetError,
  wastePerCoverTargetError,
  children,
}: ConfigOverridesProps) {
  const { t } = useTranslation();
  const { data: resolved, isLoading } = useResolvedConfig(entityType, entityId);

  if (isLoading) {
    // Mirrors the loaded layout row for row — label, control, inherited-hint line — so
    // the block does not grow when the query resolves. It used to render three bare
    // 36px skeletons against five label+control+hint rows, and the few hundred pixels
    // of growth re-centred the whole dialog: the header and the pinned submit button
    // visibly jumped on open (POS2-1142).
    const rows = [
      "Currency",
      "Timezone",
      "Weight Unit",
      "Waste Target (% of purchases)",
      "Waste per Cover Target (g)",
    ];
    return (
      <div className="space-y-4">
        <p className="text-sm font-semibold">{t("settings.configuration")}</p>
        {rows.map((label) => (
          <div key={label}>
            <p className="text-sm font-medium">{label}</p>
            <Skeleton className="mt-1.5 h-9 w-full rounded" />
            <Skeleton className="mt-1 h-4 w-2/3 rounded" />
          </div>
        ))}
        {children}
      </div>
    );
  }

  const currencyField = resolved?.config.currency;
  const timezoneField = resolved?.config.timezone;
  const weightField = resolved?.config.default_weight_unit;

  return (
    <div className="space-y-3">
      <p className="text-sm font-semibold">{t("settings.configuration")}</p>
      <div>
        <label className="text-sm font-medium" htmlFor="cfg-currency">
          {t("settings.currency")}
        </label>
        <Select
          value={currentCurrency || "inherit"}
          onValueChange={(v) => onCurrencyChange(v === "inherit" ? "" : v)}
        >
          <SelectTrigger id="cfg-currency">
            <SelectValue placeholder={t("settings.inherit")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="inherit">{t("settings.inherit")}</SelectItem>
            {withCurrentValue(t, configOptions(t, CURRENCY_OPTIONS), currentCurrency).map((c) => (
              <SelectItem key={c.value} value={c.value}>
                {c.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {!currentCurrency && <InheritedHint field={currencyField} />}
      </div>
      <div>
        <label className="text-sm font-medium" htmlFor="cfg-timezone">
          {t("settings.timezone")}
        </label>
        <Select
          value={currentTimezone || "inherit"}
          onValueChange={(v) => onTimezoneChange(v === "inherit" ? "" : v)}
        >
          <SelectTrigger id="cfg-timezone">
            <SelectValue placeholder={t("settings.inherit")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="inherit">{t("settings.inherit")}</SelectItem>
            {withCurrentValue(t, TIMEZONE_OPTIONS, currentTimezone).map((tz) => (
              <SelectItem key={tz.value} value={tz.value}>
                {tz.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {!currentTimezone && <InheritedHint field={timezoneField} />}
      </div>
      <div>
        <label className="text-sm font-medium" htmlFor="cfg-weight-unit">
          {t("settings.weightUnit")}
        </label>
        <Select
          value={currentWeightUnit || "inherit"}
          onValueChange={(v) => onWeightUnitChange(v === "inherit" ? "" : v)}
        >
          <SelectTrigger id="cfg-weight-unit">
            <SelectValue placeholder={t("settings.inherit")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="inherit">{t("settings.inherit")}</SelectItem>
            {withCurrentValue(t, configOptions(t, WEIGHT_UNIT_OPTIONS), currentWeightUnit).map(
              (u) => (
                <SelectItem key={u.value} value={u.value}>
                  {u.label}
                </SelectItem>
              ),
            )}
          </SelectContent>
        </Select>
        {!currentWeightUnit && <InheritedHint field={weightField} />}
      </div>
      <div>
        <label className="text-sm font-medium" htmlFor="cfg-waste-pct-target">
          {t("settings.wasteTargetPct")}
        </label>
        <Input
          id="cfg-waste-pct-target"
          type="number"
          step="0.1"
          min="0"
          max="100"
          placeholder={t("settings.inherit")}
          value={currentWastePctTarget ?? ""}
          onChange={(e) => onWastePctTargetChange(e.target.value)}
        />
        {wastePctTargetError && (
          <p className="mt-1 text-xs text-destructive">{wastePctTargetError}</p>
        )}
        {!wastePctTargetError && !currentWastePctTarget && (
          <InheritedHint field={resolved?.config.waste_pct_target} />
        )}
      </div>
      <div>
        <label className="text-sm font-medium" htmlFor="cfg-waste-per-cover-target">
          {t("settings.wastePerCoverTargetG")}
        </label>
        <Input
          id="cfg-waste-per-cover-target"
          type="number"
          step="1"
          min="1"
          placeholder={t("settings.inherit")}
          value={currentWastePerCoverTarget ?? ""}
          onChange={(e) => onWastePerCoverTargetChange(e.target.value)}
        />
        {wastePerCoverTargetError && (
          <p className="mt-1 text-xs text-destructive">{wastePerCoverTargetError}</p>
        )}
        {!wastePerCoverTargetError && !currentWastePerCoverTarget && (
          <InheritedHint field={resolved?.config.waste_per_cover_target_g} />
        )}
      </div>
      {children}
    </div>
  );
}
